// src/utils/globalRankings.js - VERSIÓN CORREGIDA COMPLETA

import { parseTime, calculateBestMarkVertical, calculateVerticalTieBreakData } from './ResultUtils';
import { TrackEventCodes, fieldHorizontalEvents, fieldVerticalEvents } from '../context/EventContext';

/**
 * VERSIÓN CORREGIDA: Calcula los lugares globales con desempate para eventos verticales
 * @param {Object} participantsResults - Resultados de todos los participantes por heat
 * @param {number} eventNumber - Número del evento
 * @param {number} round - Ronda del evento
 * @param {string} eventCode - Código del evento (ej: 'HJ1', '100', 'LJ1')
 * @param {Object} allHeatHeights - Objeto con alturas por heat: { "1_1_1": [{value: 1.10, isTiebreak: false}, ...] }
 * @returns {Object} Mapa de idNumber a lugar global
 */
export function computeGlobalPlaces(participantsResults, eventNumber, round, eventCode, allHeatHeights = {}) {
  console.log('[computeGlobalPlaces] Iniciando cálculo para:', { eventNumber, round, eventCode });
  console.log('[computeGlobalPlaces] allHeatHeights recibido:', allHeatHeights);

  const all = [];
  const isTrackEvent = TrackEventCodes.includes(eventCode);
  const isHorizontalEvent = fieldHorizontalEvents.includes(eventCode);
  const isVerticalEvent = fieldVerticalEvents.includes(eventCode);

  // Recopilar alturas únicas para eventos verticales
  let globalHeights = [];
  if (isVerticalEvent) {
    const allHeightsSet = new Set();
    Object.entries(allHeatHeights).forEach(([heatKey, heights]) => {
      const [eNum, rNum] = heatKey.split('_');
      if (`${eNum}` === `${eventNumber}` && `${rNum}` === `${round}`) {
        (heights || []).forEach(h => {
          allHeightsSet.add(JSON.stringify({ value: h.value, isTiebreak: h.isTiebreak || false }));
        });
      }
    });
    
    globalHeights = Array.from(allHeightsSet)
      .map(h => JSON.parse(h))
      .sort((a, b) => {
        if (a.value !== b.value) return a.value - b.value;
        return a.isTiebreak ? 1 : -1; // Alturas normales antes que de desempate
      });
    
    console.log('[computeGlobalPlaces] Alturas globales encontradas:', globalHeights);
  }

  // Recolectar todos los resultados para este evento/ronda
  Object.entries(participantsResults).forEach(([key, results]) => {
    const [eNum, rNum, hNum] = key.split('_');
    if (`${eNum}` === `${eventNumber}` && `${rNum}` === `${round}`) {
      console.log(`[computeGlobalPlaces] Procesando heat ${key}:`, Object.keys(results).length, 'participantes');
      
      Object.entries(results).forEach(([id, res]) => {
        if (isVerticalEvent) {
          // Para eventos verticales, calcular datos completos de desempate
          const bestHeight = calculateBestMarkVertical(id, res, 'vertical');
          
          if (typeof bestHeight === 'number' && bestHeight > 0) {
            // Calcular datos de desempate usando las alturas globales
            const { attemptsAtBestHeight, totalFailedAttempts } = calculateVerticalTieBreakData(
              id, 
              res, 
              bestHeight, 
              globalHeights
            );
            
            // Crear sortKey para ordenamiento preciso
            const sortKey = `${bestHeight.toFixed(3)}-${(999 - attemptsAtBestHeight).toString().padStart(3, '0')}-${(999 - totalFailedAttempts).toString().padStart(3, '0')}`;
            
            all.push({ 
              id, 
              mark: bestHeight,
              attemptsAtBestHeight,
              totalFailedAttempts,
              sortKey,
              special: res.specialStatus || res.special,
              heatKey: key // Para debugging
            });
            
            console.log(`[computeGlobalPlaces] Participante ${id}:`, {
              height: bestHeight,
              attempts: attemptsAtBestHeight,
              totalFails: totalFailedAttempts,
              sortKey
            });
          } else {
            // Estado especial o sin marca
            all.push({ 
              id, 
              mark: null, 
              special: bestHeight || res.specialStatus || res.special,
              heatKey: key
            });
            
            console.log(`[computeGlobalPlaces] Participante ${id} sin marca válida:`, {
              bestHeight,
              specialStatus: res.specialStatus,
              special: res.special
            });
          }
        } 
        else if (isTrackEvent) {
          // Para eventos de track (sin cambios)
          const tStr = res.time ?? '';
          const tRaw = res.timeRaw ?? parseTime(tStr);
          const timeRaw = (typeof tRaw === 'number' && !isNaN(tRaw)) ? tRaw : null;
          all.push({ id, mark: timeRaw, special: res.special, heatKey: key });
        } 
        else if (isHorizontalEvent) {
          // Para eventos horizontales (sin cambios)
          let bestMark = null;
          if (typeof res.bestMark === 'number') {
            bestMark = res.bestMark;
          } else if (Array.isArray(res.attempts)) {
            bestMark = res.attempts.reduce((best, att) => {
              if (!att || !att.result) return best;
              const mark = parseFloat(att.result);
              if (isNaN(mark)) return best;
              return best === null ? mark : Math.max(best, mark);
            }, null);
          }
          all.push({ id, mark: bestMark, special: res.special, heatKey: key });
        }
      });
    }
  });

  function isRealSpecial(special, mark) {
    return (
      special &&
      special !== '-' &&
      special !== 'Valid' &&
      !(special === 'Foul' && mark !== null)
    );
  }

  console.log('[computeGlobalPlaces] Total participantes recolectados:', all.length);
  console.log('[computeGlobalPlaces] Datos antes del ordenamiento:', all);

  // Ordenar con lógica específica para cada tipo de evento
  all.sort((a, b) => {
    // Primero, manejar estados especiales
    const aHasSpecial = isRealSpecial(a.special, a.mark);
    const bHasSpecial = isRealSpecial(b.special, b.mark);
    
    if (aHasSpecial && !bHasSpecial) return 1;
    if (!aHasSpecial && bHasSpecial) return -1;
    
    // Luego, manejar marcas nulas
    if (a.mark === null && b.mark !== null) return 1;
    if (a.mark !== null && b.mark === null) return -1;
    if (a.mark === null && b.mark === null) return 0;

    // Para eventos verticales, usar criterios de desempate completos
    if (isVerticalEvent) {
      // 1. Comparar alturas
      if (a.mark !== b.mark) {
        return b.mark - a.mark; // Mayor altura es mejor
      }
      
      // 2. Si las alturas son iguales, comparar intentos en esa altura
      if (a.attemptsAtBestHeight !== undefined && b.attemptsAtBestHeight !== undefined) {
        if (a.attemptsAtBestHeight !== b.attemptsAtBestHeight) {
          return a.attemptsAtBestHeight - b.attemptsAtBestHeight; // Menos intentos es mejor
        }
      }
      
      // 3. Si los intentos son iguales, comparar fallos totales
      if (a.totalFailedAttempts !== undefined && b.totalFailedAttempts !== undefined) {
        if (a.totalFailedAttempts !== b.totalFailedAttempts) {
          return a.totalFailedAttempts - b.totalFailedAttempts; // Menos fallos es mejor
        }
      }
      
      // 4. Si todo es igual, usar sortKey como fallback
      if (a.sortKey && b.sortKey) {
        return b.sortKey.localeCompare(a.sortKey);
      }
      
      return 0; // Empate total
    }
    // Para eventos de track, menor tiempo es mejor
    else if (isTrackEvent) {
      return a.mark - b.mark;
    }
    // Para eventos horizontales, mayor marca es mejor
    else if (isHorizontalEvent) {
      return b.mark - a.mark;
    }
    
    return 0;
  });

  console.log('[computeGlobalPlaces] Datos después del ordenamiento:', all);

  // Asignar lugares con manejo de empates
  const places = {};
  let currentPlace = 1;
  
  all.forEach((current, index) => {
    const { id, mark, special, attemptsAtBestHeight, totalFailedAttempts } = current;
    
    // Verificar si tiene estado especial
    const hasSpecialStatus = isRealSpecial(special, mark);
    
    // *** CORRECCIÓN: Tratar 'NM' como sin lugar válido ***
    if (mark === null || special === 'NM') {
      places[id] = '-';
    } else if (hasSpecialStatus) {
      places[id] = special;
    } else {
      // Verificar empate con el anterior
      if (index === 0) {
        places[id] = currentPlace;
      } else {
        const prev = all[index - 1];
        const prevHasSpecial = prev.special && prev.special !== '-' && prev.special !== 'Valid';
        
        // Determinar si hay empate
        let isTied = false;
        
        if (isVerticalEvent && prev.mark !== null && !prevHasSpecial) {
          // Para eventos verticales, comparar todos los criterios
          isTied = (
            prev.mark === mark &&
            (prev.attemptsAtBestHeight === attemptsAtBestHeight) &&
            (prev.totalFailedAttempts === totalFailedAttempts)
          );
        } else if (!isVerticalEvent && prev.mark !== null && !prevHasSpecial) {
          // Para otros eventos, comparar solo la marca
          isTied = prev.mark === mark;
        }
        
        if (isTied) {
          // Empate: asignar el mismo lugar
          places[id] = currentPlace;
        } else {
          // Nuevo lugar
          currentPlace = index + 1;
          places[id] = currentPlace;
        }
      }
    }
  });

  console.log('[computeGlobalPlaces] Lugares finales asignados:', places);
  console.log(`[computeGlobalPlaces] Evento ${eventCode} - ${eventNumber}/${round}:`, {
    totalParticipants: all.length,
    validMarks: all.filter(p => p.mark !== null && !(p.special && p.special !== '-' && p.special !== 'Valid')).length,
    specialStatuses: all.filter(p => p.special && p.special !== '-' && p.special !== 'Valid').length
  });

  return places; // { [idNumber]: place | '-' | specialStatus }
}

// FUNCIÓN AUXILIAR: Obtener alturas de todos los heats para un evento
export function getAllHeightsForEvent(heatHeights, eventNumber, round) {
  const allHeights = new Set();
  
  Object.entries(heatHeights).forEach(([heatKey, heights]) => {
    const [eNum, rNum] = heatKey.split('_');
    if (`${eNum}` === `${eventNumber}` && `${rNum}` === `${round}`) {
      (heights || []).forEach(h => {
        allHeights.add(JSON.stringify({ 
          value: h.value, 
          isTiebreak: h.isTiebreak || false 
        }));
      });
    }
  });
  
  return Array.from(allHeights)
    .map(h => JSON.parse(h))
    .sort((a, b) => {
      if (a.value !== b.value) return a.value - b.value;
      return a.isTiebreak ? 1 : -1;
    });
}